import React from 'react'
import {
    resolveCloudflareImageUrl,
    defaultHighResSourceSizes,
    selectClosestSourceSize,
} from './helpers'
import type {
    AspectRatio,
    ResponsiveImageProp,
    ImageEntity,
    ResolveImageUrlArgs,
    ImageSizeSpec,
    ImageSizeData,
    ImageSizeMapInterface,
    ImageBreakpoint,
} from '../shared'
import { imageDisplaySizes } from 'src/constants/image-display-sizes'
import { LibraryConfigContext } from 'src/dynamic-config/LibraryConfigContext'
import { Image, IMAGE_BREAKPOINTS } from '../image'
import { useIsInViewport } from 'src/hooks'

type DisplaySizesProp = ResponsiveImageProp<ImageSizeSpec> & {
    // require aspectRatio for base size
    base: ImageSizeSpec & { aspectRatio: AspectRatio }
}

export interface SmartImageProps {
    /**
     * Additional class(es) to be applied to the <img> tag
     */
    className?: string
    /**
     * Optional class to be added to the <picture> tag that wraps the <img> tag.
     */
    pictureTagClassName?: string
    /**
     * Image object returned from GraphQL
     */
    imageEntity: ImageEntity
    /**
     * Desired image sizes to display for different breakpoints.
     * `base` breakpoint is required; all other breakpoints are optional.
     */
    displaySizes: DisplaySizesProp
    /**
     * Callback that returns the image URL for a specific size
     */
    resolveImageUrl?: (args: ResolveImageUrlArgs) => string
    /**
     * Used for the cloudflare image resizer and any other resizing services - it should
     * be the base URL before the parameters specific to each image that comes after it.
     */
    imageResizerBaseUrl?: string
    /**
     * If true, the image will be cropped into a circle (border-radius 100%)
     */
    ellipse?: boolean
    /**
     * Sets the object-fit CSS property on the <img> element. @see https://developer.mozilla.org/en-US/docs/Web/CSS/object-fit
     */
    objectFit?: 'contain' | 'cover' | 'fill' | 'scale-down' | 'none'
    /**
     * Sets the object-position CSS property on the <img> element. @see https://developer.mozilla.org/en-US/docs/Web/CSS/object-position
     */
    objectPosition?:
        | 'bottom'
        | 'center'
        | 'left'
        | 'left-bottom'
        | 'left-top'
        | 'right'
        | 'right-bottom'
        | 'right-top'
        | 'top'

    /**
     * When true, the image will be displayed at 100% of the width of its container.
     * @default true
     */
    fullWidth?: boolean
    /**
     * Set this to true if you want to write your own CSS classes to specify width and/or height
     */
    specifyDisplayDimensionsManually?: boolean
    availableSourceSizes?: ImageSizeData
    ref?: React.Ref<HTMLImageElement>
    /**
     * When true, the image will loading will be deferred until its within the viewport.
     * NOTE: This should only be used as an override to bypass lazy loading.
     * @default true
     */
    allowLazy?: boolean
}

// the weird syntax is currently necessary to make the `compileTime` vite plugin
// work in storybook (something to do with @storybook/react-vite)
let defaultSourceSizes: ImageSizeMapInterface
async function getDefaultSourceSizes() {
    const tmp = compileTime(defaultHighResSourceSizes())
    defaultSourceSizes = tmp
}
getDefaultSourceSizes()

const DEFAULT_IMAGE_RESIZER_BASE_URL_FOR_CLOUDFLARE =
    'https://images.artnet.com/'

export function SmartImage({
    className,
    pictureTagClassName,
    imageEntity,
    resolveImageUrl: resolveImageUrlProp,
    imageResizerBaseUrl,
    displaySizes: sizes,
    ellipse,
    objectFit = 'scale-down',
    objectPosition = 'center',
    fullWidth = true,
    specifyDisplayDimensionsManually = false,
    availableSourceSizes: availableSourceSizesProp,
    ref,
    allowLazy = true,
}: Readonly<SmartImageProps>) {
    const libraryConfig = React.useContext(LibraryConfigContext)
    const elementRef = React.useRef<HTMLImageElement | null>(null)
    const inViewport = useIsInViewport(elementRef)

    // Only enable lazy loading if allowLazy is true AND the image has not yet reached the viewport threshold
    const loading = allowLazy && !inViewport ? 'lazy' : undefined

    if (!imageResizerBaseUrl) {
        imageResizerBaseUrl = libraryConfig.imageResizerBaseUrl
    }

    // Determine the resolveImageUrl function to use. Normally this would be specified in the
    // LibraryConfig context, but it can be overridden with the `resolveImageUrl` prop if desired.
    let resolveImageUrl: (args: ResolveImageUrlArgs) => string
    if (resolveImageUrlProp) {
        resolveImageUrl = resolveImageUrlProp
    } else {
        if (libraryConfig.imageUrlResolver) {
            resolveImageUrl = libraryConfig.imageUrlResolver
        } else {
            resolveImageUrl = resolveCloudflareImageUrl
            if (!imageResizerBaseUrl) {
                imageResizerBaseUrl =
                    DEFAULT_IMAGE_RESIZER_BASE_URL_FOR_CLOUDFLARE
            }
        }
    }

    const availableSourceSizes = availableSourceSizesProp
        ? (new Map(
              Object.entries(availableSourceSizesProp)
          ) as ImageSizeMapInterface)
        : defaultSourceSizes

    const completedSizes = completeSizes(sizes)
    const imgInlineStyles: Record<string, string> = {}

    const imgClassNames = className ? [className] : []
    if (fullWidth && !specifyDisplayDimensionsManually) {
        imgClassNames.push('w-full')
    }

    if (!fullWidth) {
        imgClassNames.push(
            // these are defined statically so that tailwind will see them during compilation
            'max-w-[--img-w-base]',
            '2xs:max-w-[--img-w-2xs]',
            'md:max-w-[--img-w-md]',
            'lg:max-w-[--img-w-lg]'
        )

        let sizeAtPrevBreakpoint = completedSizes.base
        for (const breakpoint of IMAGE_BREAKPOINTS) {
            const sizeAtBreakpoint =
                completedSizes[breakpoint] ?? sizeAtPrevBreakpoint
            sizeAtPrevBreakpoint = sizeAtBreakpoint

            const dimensions = imageDisplaySizes.getDimensions(
                sizeAtBreakpoint.aspectRatio,
                sizeAtBreakpoint.size
            )
            if (!dimensions) {
                if (process.env.NODE_ENV === 'development') {
                    throw Error(
                        `Error retrieving dimensions for display size '${sizeAtBreakpoint.size}'`
                    )
                }
                continue
            }
            imgInlineStyles['--img-w-' + breakpoint] = dimensions.width + 'px'
        }
    }

    const responsiveUrls = resolveResponsiveUrls(
        {} as ResponsiveImageProp<string>,
        IMAGE_BREAKPOINTS.values()
    )

    imgClassNames.push(
        ...resolveAspectRatioClasses([], IMAGE_BREAKPOINTS.values())
    )

    function resolveResponsiveUrls(
        urls: ResponsiveImageProp<string>,
        breakpointIterator: Iterator<ImageBreakpoint>,
        prevUrl?: string
    ): ResponsiveImageProp<string> {
        const nextResult = breakpointIterator.next()
        const isLastBreakpoint = nextResult.done
        const breakpoint = nextResult.value as ImageBreakpoint

        const sizeSpec = completedSizes[breakpoint]
        if (!sizeSpec) {
            return isLastBreakpoint
                ? urls
                : resolveResponsiveUrls(urls, breakpointIterator, prevUrl)
        }

        const sourceDimensions = selectClosestSourceSize({
            displaySizeName: sizeSpec.size,
            aspectRatio: sizeSpec.aspectRatio,
            sortedSourceSizes: availableSourceSizes,
        })

        let urlForThisBreakpoint = ''
        if (sourceDimensions) {
            urlForThisBreakpoint = resolveImageUrl({
                imageFilePath: imageEntity.baseImageUrl,
                dimensions: sourceDimensions,
                imageResizerBaseUrl,
                crop: sizeSpec.crop,
            })
        } else if (process.env.NODE_ENV === 'development') {
            throw Error(
                `Error determining closest source size for display size '${sizeSpec.size}'`
            )
        }

        // optimization: only add another <source> element if it's a different URL
        if (urlForThisBreakpoint !== prevUrl) {
            urls[breakpoint] = urlForThisBreakpoint
        }

        if (!isLastBreakpoint) {
            return resolveResponsiveUrls(
                urls,
                breakpointIterator,
                urlForThisBreakpoint
            )
        }

        return urls
    }

    // Add aspect ratio tailwind class to prevent cumulative layout shift (CLS).
    // Note that either the width or the height must also be set in order for this to work
    // to prevent CLS; this component sets the width to 100% by default thanks to the
    // `fullWidth` prop.
    function resolveAspectRatioClasses(
        classes: string[],
        breakpointIterator: Iterator<ImageBreakpoint>,
        aspectRatioPrevBreakpoint?: AspectRatio
    ): string[] {
        const breakpoint = breakpointIterator.next().value as ImageBreakpoint
        const sizeSpec = completedSizes[breakpoint]

        const buildClassName = (breakpoint: ImageBreakpoint) => {
            if (
                !sizeSpec ||
                sizeSpec.aspectRatio === aspectRatioPrevBreakpoint
            ) {
                return ''
            }
            const prefix = breakpoint === 'base' ? '' : `${breakpoint}:`
            return `${prefix}aspect-[${sizeSpec.aspectRatio}]`
        }

        const className = buildClassName(breakpoint)
        className && classes.push(className)

        if (!breakpointIterator.next().done) {
            return resolveAspectRatioClasses(
                classes,
                breakpointIterator,
                sizeSpec?.aspectRatio
            )
        }

        return classes
    }

    return (
        <Image
            ref={(node) => {
                // Handle both the forwarded ref and our elementRef
                if (typeof ref === 'function') {
                    ref(node)
                } else if (ref) {
                    ref.current = node
                }
                elementRef.current = node
            }}
            responsiveUrls={responsiveUrls}
            pictureTagClassName={pictureTagClassName}
            className={imgClassNames.join(' ')}
            style={imgInlineStyles}
            alt={imageEntity.alt ?? ''}
            title={imageEntity.title ?? undefined}
            ellipse={ellipse}
            objectFit={objectFit}
            objectPosition={objectPosition}
            loading={loading}
        />
    )
}

type CompletedSizesProp = ResponsiveImageProp<
    ImageSizeSpec & { aspectRatio: AspectRatio }
>

// If the user omitted aspect ratios for any breakpoints, fill them in for them.
//
// Note: this function mutates the `sizes` object, which slightly more performant
// that creating a new object, but this could definitely be refactored if desired
// since the performance difference is negligible.
function completeSizes(sizes: DisplaySizesProp): CompletedSizesProp {
    let aspectRatioPrevBreakpoint = sizes.base.aspectRatio
    for (const size of Object.values(sizes)) {
        if (!size.aspectRatio) {
            size.aspectRatio = aspectRatioPrevBreakpoint
        } else {
            aspectRatioPrevBreakpoint = size.aspectRatio
        }
    }
    return sizes as CompletedSizesProp
}
